import { COOKIE_CONFIG, COOKIE_OPTIONS } from '@/config/cookie.config';
import { SERVER_URL } from '@/constants';
import axios from 'axios';
import { clearCookies, getCookie, removeCookie, setCookie } from './cookies';
import { auth } from './firebase';
import { notifications } from './notifications';

/**
 * Axios instance configured with base URL and default headers.
 *
 * The base URL is constructed using environment variables:
 * - VITE_API_URL: The base API URL.
 * - VITE_API_VERSION: The API version.
 *
 * Default headers:
 * - 'Content-Type': 'application/json'
 *
 * @type {import('axios').AxiosInstance}
 */

/**
 * Get stored authentication token
 * @returns {string|null} Authentication token or null
 */
const getAuthToken = () => {
  return getCookie(COOKIE_CONFIG.AUTH_TOKEN);
};

/**
 * Store authentication token securely
 * @param {string} token - Authentication token
 */
const setAuthToken = (token) => {
  setCookie(COOKIE_CONFIG.AUTH_TOKEN, token, COOKIE_OPTIONS.AUTH_TOKEN);
};

/**
 * Remove authentication token
 */
const removeAuthToken = () => {
  removeCookie(COOKIE_CONFIG.AUTH_TOKEN);
};

/**
 * Refresh Firebase token and update cookie
 * @returns {Promise<string|null>} New token or null if failed
 */
const refreshFirebaseToken = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('No Firebase user found for token refresh');
      return null;
    }

    // Force token refresh
    const newToken = await user.getIdToken(true);

    if (newToken) {
      setAuthToken(newToken);
      console.log('Firebase token refreshed successfully');
      return newToken;
    }

    return null;
  } catch (error) {
    console.error('Failed to refresh Firebase token:', error);

    // Clear all auth cookies on refresh failure
    clearCookies();

    // Show user-friendly error
    notifications.showError({
      message: 'Session Expired',
      description: 'Please sign in again to continue',
    });

    return null;
  }
};

/**
 * Handle authentication errors
 * @param {Object} error - Axios error object
 * @param {Object} originalRequest - Original request config
 */
const handleAuthError = async (error, originalRequest) => {
  console.error(`Token expired or invalid, attempting to refresh...`);
  // Prevent infinite retry loops
  if (originalRequest._retry) {
    clearCookies();
    notifications.showError({
      message: 'Authentication Failed',
      description: 'Please sign in again',
    });
    throw error;
  }

  originalRequest._retry = true;

  try {
    const newToken = await refreshFirebaseToken();

    if (newToken) {
      // Retry original request with new token
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      const response = await axios(originalRequest);
      return response.data;
    } else {
      throw new Error('Failed to refresh token');
    }
  } catch (refreshError) {
    console.error('Token refresh failed:', refreshError);
    clearCookies();
    throw error;
  }
};

/**
 * Create axios instance with base configuration
 */
const axiosInstance = axios.create({
  baseURL: SERVER_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

/**
 * REQUEST INTERCEPTOR
 * Automatically adds authentication token to requests
 */
axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      // Get token from secure cookie
      const token = getAuthToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add request timestamp for debugging
      config.metadata = {
        startTime: new Date().getTime(),
        url: config.url,
      };

      return config;
    } catch (error) {
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    }
  },
  (error) => {
    console.error('Request interceptor rejection:', error);
    return Promise.reject(error);
  }
);

/**
 * RESPONSE INTERCEPTOR
 * Handles authentication errors and token refresh
 */
axiosInstance.interceptors.response.use(
  (response) => {
    // Log response time for debugging (only in development)
    if (import.meta.env.DEV && response.config.metadata) {
      const endTime = new Date().getTime();
      const duration = endTime - response.config.metadata.startTime;
      console.log(
        `API Request to ${response.config.metadata.url} took ${duration}ms`
      );
    }

    // Return the data directly (as your original code does)
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors
    if (!error.response) {
      notifications.showError({
        message: 'Network Error',
        description: 'Please check your internet connection and try again',
      });
      return Promise.reject(error);
    }

    // Handle specific HTTP status codes
    switch (error.response.status) {
      case 401:
        // Unauthorized - attempt token refresh
        try {
          return await handleAuthError(error, originalRequest);
        } catch (authError) {
          // Token refresh failed, let the auth context handle sign out
          return Promise.reject(authError);
        }

      case 403:
        // Forbidden - user doesn't have permission
        notifications.showError({
          message: 'Access Denied',
          description:
            error.response?.data?.message ||
            'You do not have permission to perform this action',
        });
        break;

      case 404:
        // Not found - resource doesn't exist
        notifications.showError({
          message: 'Not Found',
          description:
            error.response?.data?.message ||
            'The requested resource was not found',
        });
        break;

      case 429:
        // Too many requests - rate limiting
        notifications.showError({
          message: 'Too Many Requests',
          description: 'Please wait a moment before trying again',
        });
        break;

      case 500:
        // Internal server error
        notifications.showError({
          message: 'Server Error',
          description:
            'An internal server error occurred. Please try again later.',
        });
        break;

      case 502:
      case 503:
      case 504:
        // Server unavailable
        notifications.showError({
          message: 'Service Unavailable',
          description:
            'The service is temporarily unavailable. Please try again later.',
        });
        break;

      default: {
        // Generic error handling
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          'An unexpected error occurred';

        notifications.showError({
          message: 'Error',
          description: errorMessage,
        });
        break;
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Export utility functions for external use
 */
export { getAuthToken, refreshFirebaseToken, removeAuthToken, setAuthToken };

export default axiosInstance;
